import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { BedDouble, ChevronRight, ListChecks, ShieldAlert } from 'lucide-react';
import { wardTasksPath } from '@his/shared';
import { tr } from '@/lib/i18n-core';
import { get, qs } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { useWorkingBranchId } from '@/lib/branch';
import { EmptyState, PageHeader } from '@/components/ui/layout';
import { Alert, Badge, Skeleton } from '@/components/ui/primitives';
import { TASK_READ_PERMISSIONS, useStaffDuty } from './task-queries';
import { pickerWards, wardPickerKey, type WardOption } from './ward-picker';

const SKELETON_CARDS = ['w1', 'w2', 'w3'];

export function WardPickerPage() {
  const { can, hasModule } = useAuth();
  const allowed = hasModule('ipd') && can(...TASK_READ_PERMISSIONS);
  const [params] = useSearchParams();
  const showAll = params.get('all') === '1';
  const branchId = useWorkingBranchId();
  const duty = useStaffDuty(allowed);
  const dutyWards = duty.data?.wards ?? [];
  const settled = duty.isSuccess || duty.isError;
  const redirect = !showAll && duty.isSuccess && dutyWards.length === 1 ? dutyWards[0] : undefined;
  const fallback = useQuery({
    queryKey: wardPickerKey(branchId),
    queryFn: () => get<WardOption[]>(`/ipd/wards${qs({ branchId: branchId || undefined, limit: 200 })}`),
    enabled: allowed && settled && !redirect && can('bed.read'),
    staleTime: 5 * 60_000,
  });

  if (!allowed) {
    return (
      <>
        <PageHeader title={tr('ipd', 'io_ward_picker_title')} />
        <EmptyState icon={ShieldAlert} title={tr('ipd', 'io_ward_not_allowed')} />
      </>
    );
  }
  if (redirect) return <Navigate to={wardTasksPath(redirect.id)} replace />;

  const wards = pickerWards(dutyWards, fallback.data ?? []);
  const loading = duty.isLoading || fallback.isLoading;

  return (
    <>
      <PageHeader title={tr('ipd', 'io_ward_picker_title')} description={tr('ipd', 'io_ward_picker_desc')} />
      {duty.isError && <Alert variant="warning" className="mb-3">{describeError(duty.error)}</Alert>}
      {fallback.isError && <Alert variant="destructive" className="mb-3">{describeError(fallback.error)}</Alert>}
      {loading && (
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {SKELETON_CARDS.map((key) => (
            <Skeleton key={key} className="h-16 w-full rounded-xl" />
          ))}
        </div>
      )}
      {!loading && wards.length === 0 && <EmptyState icon={BedDouble} title={tr('ipd', 'io_ward_none_title')} description={tr('ipd', 'io_ward_none_desc')} />}
      {wards.length > 0 && (
        <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {wards.map((ward) => (
            <li key={ward.id}>
              <Link
                to={wardTasksPath(ward.id)}
                className="focus-ring flex items-center gap-3 rounded-xl border bg-card px-3 py-3 shadow-sm transition-colors hover:border-primary/40 hover:bg-muted/40"
              >
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary" aria-hidden>
                  <ListChecks className="h-4 w-4" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium">{ward.name}</span>
                  <span className="block truncate text-xs text-muted-foreground">{ward.code}</span>
                </span>
                {ward.onDuty && <Badge variant="success">{tr('ipd', 'io_ward_on_duty')}</Badge>}
                <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground rtl:-scale-x-100" aria-hidden />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
